/**
 * Gyro Admin's copy of gyro-svc's day-outfit date engine (services/gyro-svc/src/desktopMascotDays.ts): everything below
 * this comment is that file, byte for byte. The console reads day outfits with it (lib/dayOutfits.ts): each rule in
 * words, its next dates and its tab as the clock moves, which outfit is swapped in today, the form's live "Next: 3 dates
 * (Pacific)", and the warnings. apps/admin/test/mascot-days.test.ts checks that this file is the service's file after this
 * comment, and that both give the same answers for every rule kind from 2026 to 2030. Change the service's file first,
 * then copy it here; never edit below this comment on its own.
 */

/** The zone for an app that sends none, and for anything that is not a valid IANA zone name: the company's. */
export const DAY_DEFAULT_ZONE = 'America/Los_Angeles';
/** The longest day outfit run, in days (Hanukkah's eight fit). */
export const DAY_MAX_SPAN = 14;
/** The longest day greeting, in code points: an emoji is one to several (❤️ and a flag two, a joined emoji more). */
export const DAY_GREETING_MAX = 140;
/** The most years an explicit rule lists (each a date IT enters). */
export const DAY_MAX_EXPLICIT_YEARS = 30;
/** The years an explicit or one-off date may name. */
export const DAY_MIN_YEAR = 2020;
export const DAY_MAX_YEAR = 2100;
/** How far an Easter rule may move from Easter Sunday, in days (Mardi Gras is -47). */
export const DAY_MAX_EASTER_OFFSET = 70;

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * When a day outfit comes. Every kind but 'once' comes back every year:
 * - fixed: the same month and day every year (February 29 only in leap years);
 * - nth-weekday: the nth weekday of a month, 0 = Sunday, n = 1 to 4 or -1 for the last one (Thanksgiving: November,
 *   Thursday, 4);
 * - easter: that many days from Western (Gregorian) Easter Sunday (Mardi Gras: -47);
 * - explicit: the start date IT enters for each year (Hanukkah, Lunar New Year, Super Bowl Sunday), each lasting
 *   spanDays; confirmEachYear marks a date that is announced later and must be checked every year;
 * - once: one date, spanDays long, never again.
 */
export type DayRule =
  | { kind: 'fixed'; month: number; day: number }
  | { kind: 'nth-weekday'; month: number; weekday: number; n: number }
  | { kind: 'easter'; offsetDays: number }
  | { kind: 'explicit'; dates: Record<string, string>; spanDays: number; confirmEachYear: boolean }
  | { kind: 'once'; date: string; spanDays: number };

/** One occurrence of a rule: its first civil date and how many days it lasts. */
export interface DayOccurrence {
  start: number;
  span: number;
}

export const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'] as const;
export const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;
/** The last day a month can have in any year, February counted as 29. */
const MONTH_MAX_DAY = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
const NTH = new Set([1, 2, 3, 4, -1]);

// ---------------------------------------------------------------------------------------------------------- dates

/** The civil day number of a calendar date (whole days since 1970-01-01). */
export function civilOf(year: number, month: number, day: number): number {
  return Date.UTC(year, month - 1, day) / DAY;
}

/** The calendar date of a civil day number. */
export function civilParts(civil: number): { year: number; month: number; day: number } {
  const date = new Date(civil * DAY);
  return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate() };
}

/** 'YYYY-MM-DD' for a civil day number. */
export function isoOfCivil(civil: number): string {
  const { year, month, day } = civilParts(civil);
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** The civil day number of a real 'YYYY-MM-DD' date; undefined for anything else (2027-02-30 included). */
export function civilOfIso(value: unknown): number | undefined {
  if (typeof value !== 'string') return undefined;
  const match = ISO_DATE_RE.exec(value);
  if (!match) return undefined;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return undefined;
  const civil = civilOf(year, month, day);
  const back = civilParts(civil);
  return back.year === year && back.month === month && back.day === day ? civil : undefined;
}

/** The weekday of a civil day number: 0 = Sunday (1970-01-01 was a Thursday). */
export function weekdayOfCivil(civil: number): number {
  return (((civil % 7) + 7) % 7 + 4) % 7;
}

export function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

/** Western (Gregorian) Easter Sunday of a year, as a civil day number: the anonymous Gregorian algorithm. */
export function easterSunday(year: number): number {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return civilOf(year, month, day);
}

/** The nth weekday (0 = Sunday) of a month, n = 1 to 4, or -1 for the last one. */
export function nthWeekdayOf(year: number, month: number, weekday: number, n: number): number {
  if (n === -1) {
    const last = civilOf(year, month + 1, 1) - 1;
    return last - ((weekdayOfCivil(last) - weekday + 7) % 7);
  }
  const first = civilOf(year, month, 1);
  return first + ((weekday - weekdayOfCivil(first) + 7) % 7) + 7 * (n - 1);
}

// ---------------------------------------------------------------------------------------------------------- rules

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasExactKeys(value: Record<string, unknown>, keys: readonly string[]): boolean {
  const own = Object.keys(value);
  return own.length === keys.length && keys.every((key) => Object.hasOwn(value, key));
}

function intIn(value: unknown, min: number, max: number): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= min && value <= max;
}

/** A date an explicit or one-off rule may name: a real date from DAY_MIN_YEAR to DAY_MAX_YEAR. */
function ruleDate(value: unknown): number | undefined {
  const civil = civilOfIso(value);
  if (civil === undefined) return undefined;
  const { year } = civilParts(civil);
  return year >= DAY_MIN_YEAR && year <= DAY_MAX_YEAR ? civil : undefined;
}

/**
 * A stored or submitted rule, checked field by field, as a fresh copy; undefined for anything else, including a kind
 * or a field this release does not know (a rule written by a newer release is never guessed at).
 */
export function parseDayRule(raw: unknown): DayRule | undefined {
  if (!isRecord(raw)) return undefined;
  switch (raw.kind) {
    case 'fixed':
      if (!hasExactKeys(raw, ['kind', 'month', 'day']) || !intIn(raw.month, 1, 12) || !intIn(raw.day, 1, MONTH_MAX_DAY[raw.month - 1]!)) return undefined;
      return { kind: 'fixed', month: raw.month, day: raw.day };
    case 'nth-weekday':
      if (!hasExactKeys(raw, ['kind', 'month', 'weekday', 'n']) || !intIn(raw.month, 1, 12) || !intIn(raw.weekday, 0, 6)
          || typeof raw.n !== 'number' || !NTH.has(raw.n)) return undefined;
      return { kind: 'nth-weekday', month: raw.month, weekday: raw.weekday, n: raw.n };
    case 'easter':
      if (!hasExactKeys(raw, ['kind', 'offsetDays']) || !intIn(raw.offsetDays, -DAY_MAX_EASTER_OFFSET, DAY_MAX_EASTER_OFFSET)) return undefined;
      return { kind: 'easter', offsetDays: raw.offsetDays };
    case 'explicit': {
      if (!hasExactKeys(raw, ['kind', 'dates', 'spanDays', 'confirmEachYear']) || !isRecord(raw.dates)
          || !intIn(raw.spanDays, 1, DAY_MAX_SPAN) || typeof raw.confirmEachYear !== 'boolean') return undefined;
      const years = Object.keys(raw.dates);
      if (years.length < 1 || years.length > DAY_MAX_EXPLICIT_YEARS) return undefined;
      const dates: Record<string, string> = {};
      for (const year of years.sort()) {
        const value = raw.dates[year];
        const civil = ruleDate(value);
        if (!/^\d{4}$/.test(year) || civil === undefined || civilParts(civil).year !== Number(year)) return undefined;
        dates[year] = value as string;
      }
      return { kind: 'explicit', dates, spanDays: raw.spanDays, confirmEachYear: raw.confirmEachYear };
    }
    case 'once':
      if (!hasExactKeys(raw, ['kind', 'date', 'spanDays']) || ruleDate(raw.date) === undefined || !intIn(raw.spanDays, 1, DAY_MAX_SPAN)) return undefined;
      return { kind: 'once', date: raw.date as string, spanDays: raw.spanDays };
    default:
      return undefined;
  }
}

/**
 * A rule as the import manifest writes it (Y:\scratch\keep\gyro-day-outfits\manifest.json): weekday names ("Thursday")
 * and n = "last" for nth-weekday, and an explicit rule that may leave out confirmEachYear (false). Undefined when it
 * is not a valid rule after that mapping.
 */
export function dayRuleFromManifest(raw: unknown): DayRule | undefined {
  if (!isRecord(raw)) return undefined;
  if (raw.kind === 'nth-weekday') {
    const weekday = typeof raw.weekday === 'string'
      ? WEEKDAY_NAMES.findIndex((name) => name.toLowerCase() === String(raw.weekday).trim().toLowerCase())
      : raw.weekday;
    const n = raw.n === 'last' ? -1 : raw.n;
    return parseDayRule({ ...raw, weekday, n });
  }
  if (raw.kind === 'explicit' && !Object.hasOwn(raw, 'confirmEachYear')) return parseDayRule({ ...raw, confirmEachYear: false });
  return parseDayRule(raw);
}

/** The occurrence of a rule that starts in a year, if there is one. */
export function occurrence(rule: DayRule, year: number): DayOccurrence | undefined {
  switch (rule.kind) {
    case 'fixed':
      if (rule.month === 2 && rule.day === 29 && !isLeapYear(year)) return undefined;
      return { start: civilOf(year, rule.month, rule.day), span: 1 };
    case 'nth-weekday':
      return { start: nthWeekdayOf(year, rule.month, rule.weekday, rule.n), span: 1 };
    case 'easter':
      return { start: easterSunday(year) + rule.offsetDays, span: 1 };
    case 'explicit': {
      const key = String(year);
      const start = Object.hasOwn(rule.dates, key) ? civilOfIso(rule.dates[key]) : undefined;
      return start === undefined ? undefined : { start, span: rule.spanDays };
    }
    case 'once': {
      const start = civilOfIso(rule.date);
      return start !== undefined && civilParts(start).year === year ? { start, span: rule.spanDays } : undefined;
    }
  }
}

/** Whether a rule's outfit is on a civil date (an occurrence that started this year or, spanning New Year, last year). */
export function coversCivil(rule: DayRule, civil: number): boolean {
  const { year } = civilParts(civil);
  return [year - 1, year].some((y) => {
    const found = occurrence(rule, y);
    return found !== undefined && found.start <= civil && civil < found.start + found.span;
  });
}

/** The next `count` occurrences of a rule that are going on at `fromCivil` or start later, in date order. */
export function nextOccurrences(rule: DayRule, fromCivil: number, count: number): DayOccurrence[] {
  const found: DayOccurrence[] = [];
  for (let year = civilParts(fromCivil).year - 1; year <= DAY_MAX_YEAR && found.length < count; year += 1) {
    const next = occurrence(rule, year);
    if (next && next.start + next.span > fromCivil) found.push(next);
  }
  return found;
}

/**
 * A rule in plain words for the admin console and the import's dry run: "Every year on December 25", "Fourth Thursday of
 * November, every year", "47 days before Easter Sunday, every year", "8 days from the date IT enters each year", "Once,
 * on 14 March 2027" (a date written out, as people read it, never 2027-03-14).
 */
export function describeDayRule(rule: DayRule): string {
  const ordinal = ['', 'First', 'Second', 'Third', 'Fourth'];
  switch (rule.kind) {
    case 'fixed':
      return `Every year on ${MONTH_NAMES[rule.month - 1]} ${rule.day}${rule.month === 2 && rule.day === 29 ? ' (leap years only)' : ''}`;
    case 'nth-weekday':
      return `${rule.n === -1 ? 'Last' : ordinal[rule.n]} ${WEEKDAY_NAMES[rule.weekday]} of ${MONTH_NAMES[rule.month - 1]}, every year`;
    case 'easter':
      if (rule.offsetDays === 0) return 'Easter Sunday, every year';
      return `${Math.abs(rule.offsetDays)} day${Math.abs(rule.offsetDays) === 1 ? '' : 's'} ${rule.offsetDays < 0 ? 'before' : 'after'} Easter Sunday, every year`;
    case 'explicit':
      return `${rule.spanDays === 1 ? 'On the date' : `${rule.spanDays} days from the date`} IT enters each year${rule.confirmEachYear ? ' (confirm each year)' : ''}`;
    case 'once': {
      const civil = civilOfIso(rule.date);
      const parts = civil === undefined ? undefined : civilParts(civil);
      const date = parts ? `${parts.day} ${MONTH_NAMES[parts.month - 1]} ${parts.year}` : rule.date;
      return rule.spanDays === 1 ? `Once, on ${date}` : `Once, ${rule.spanDays} days from ${date}`;
    }
  }
}

/**
 * Where a day outfit is in its life on a calendar date (the console's tabs, judged by the Pacific calendar there):
 * 'today' while it is on, 'scheduled' when a later date is known, 'needs-date' for a rule with dates IT enters and none
 * to come, 'past' for a one-off that has gone.
 */
export type DayLifeState = 'today' | 'scheduled' | 'needs-date' | 'past';

/** A day outfit's DayLifeState on a calendar date. */
export function dayLifeState(rule: DayRule, today: number): DayLifeState {
  if (coversCivil(rule, today)) return 'today';
  if (nextOccurrences(rule, today, 1).length > 0) return 'scheduled';
  return rule.kind === 'once' ? 'past' : 'needs-date';
}

/**
 * Whether a day outfit's day is going on somewhere on Earth at `now` (everywhereWindow: from its start in UTC+14 to its
 * end in UTC-12), with `today` the date where it is judged (the Pacific calendar in the console).
 */
export function showingSomewhere(rule: DayRule, today: number, now: number): boolean {
  const { year } = civilParts(today);
  return [year - 1, year, year + 1].some((candidate) => {
    const found = occurrence(rule, candidate);
    if (!found) return false;
    const window = everywhereWindow(found.start, found.start + found.span);
    return window.startMs <= now && now < window.endMs;
  });
}

// ---------------------------------------------------------------------------------------------------------- owners

/** A day outfit as the engine sees it: its id, its rule, and its place in catalog order (smaller comes first). */
export interface DayEntry {
  readonly id: string;
  readonly rule: DayRule;
  readonly order: number;
}

/** A day outfit on one date, with the occurrence that puts it there. */
export interface DayPlacement {
  readonly entry: DayEntry;
  readonly start: number;
  readonly span: number;
}

/** A run of days that one day outfit holds: from startCivil (included) to endCivil (not included). */
export interface DayRun {
  readonly winner: DayEntry;
  readonly startCivil: number;
  readonly endCivil: number;
}

/** A date with more than one day outfit: a clash (same length, first in catalog order wins) or a shorter one inside a longer one. */
export interface DayConflict {
  readonly civil: number;
  readonly kind: 'clash' | 'span-note';
  readonly winnerId: string;
  readonly otherIds: string[];
}

/**
 * Which day outfit owns each date. On a date with several, the shortest occurrence wins (a one-day outfit inside
 * Hanukkah's eight days takes its own date), then the first in catalog order (Valentine's Day, order 1, over Super Bowl
 * Sunday, order 12, on 2027-02-14). The others stay in the gallery that day; only the swap and the greeting go to the
 * winner. Built lazily per year, for the entries it was made with (a new catalog makes a new index).
 */
export class DayIndex {
  private readonly years = new Map<number, Map<number, DayPlacement[]>>();

  constructor(private readonly entries: readonly DayEntry[]) {}

  private byYear(year: number): Map<number, DayPlacement[]> {
    const known = this.years.get(year);
    if (known) return known;
    const map = new Map<number, DayPlacement[]>();
    for (const entry of this.entries) {
      const found = occurrence(entry.rule, year);
      if (!found) continue;
      for (let civil = found.start; civil < found.start + found.span; civil += 1) {
        const list = map.get(civil);
        const placement = { entry, start: found.start, span: found.span };
        if (list) list.push(placement);
        else map.set(civil, [placement]);
      }
    }
    if (this.years.size >= 8) this.years.clear();
    this.years.set(year, map);
    return map;
  }

  /** Every day outfit on a date, the winner first. */
  covering(civil: number): DayPlacement[] {
    const { year } = civilParts(civil);
    const found = [...(this.byYear(year - 1).get(civil) ?? []), ...(this.byYear(year).get(civil) ?? [])];
    return found.sort((a, b) => a.span - b.span || a.entry.order - b.entry.order || (a.entry.id < b.entry.id ? -1 : a.entry.id > b.entry.id ? 1 : 0));
  }

  /** The day outfit that owns a date, if any. */
  winner(civil: number): DayPlacement | undefined {
    return this.covering(civil)[0];
  }

  /** The days around `civil` that its winner holds without a break (at most DAY_MAX_SPAN each way). */
  run(civil: number): DayRun | undefined {
    const first = this.winner(civil);
    if (!first) return undefined;
    const id = first.entry.id;
    let start = civil;
    let end = civil + 1;
    for (let step = 0; step < DAY_MAX_SPAN && this.winner(start - 1)?.entry.id === id; step += 1) start -= 1;
    for (let step = 0; step < DAY_MAX_SPAN && this.winner(end)?.entry.id === id; step += 1) end += 1;
    return { winner: first.entry, startCivil: start, endCivil: end };
  }

  /** Dates from `fromCivil` for `days` days with more than one day outfit, for the console's warnings. */
  conflicts(fromCivil: number, days: number): DayConflict[] {
    const found: DayConflict[] = [];
    for (let civil = fromCivil; civil < fromCivil + days; civil += 1) {
      const [winner, ...others] = this.covering(civil);
      if (!winner || others.length === 0) continue;
      const same = others.filter((other) => other.span === winner.span).map((other) => other.entry.id);
      const longer = others.filter((other) => other.span > winner.span).map((other) => other.entry.id);
      if (same.length) found.push({ civil, kind: 'clash', winnerId: winner.entry.id, otherIds: same });
      if (longer.length) found.push({ civil, kind: 'span-note', winnerId: winner.entry.id, otherIds: longer });
    }
    return found;
  }
}

// ---------------------------------------------------------------------------------------------------------- zones

/** An IANA zone name as clients send it: letters, digits, _ + - and up to two slashes; never an offset like +05:00. */
const ZONE_RE = /^[A-Za-z][A-Za-z0-9_+-]*(?:\/[A-Za-z0-9_+-]+){0,2}$/;
const ZONES_MAX = 64;
const REJECTED_MAX = 256;
const STARTS_MAX = 4096;

interface Zone {
  readonly name: string;
  readonly format: Intl.DateTimeFormat;
}

/**
 * Formatters by the name as sent, lowercased (Intl reads zone names in any letter case, so `america/new_york` and
 * `America/New_York` are one entry and a caller cannot defeat the cache by changing case), least recently used out first,
 * never the default zone; and names Intl refused, so neither grows without bound. A name Intl refuses costs one failed
 * construction once, until REJECTED_MAX newer refusals push it out.
 */
const zones = new Map<string, Zone>();
const rejected = new Set<string>();
const DEFAULT_KEY = DAY_DEFAULT_ZONE.toLowerCase();
/** Each zone's day starts already worked out (the zone's rules do not change while the process runs). */
const starts = new Map<string, number>();

function zoneFor(name: string): Zone | undefined {
  const key = name.toLowerCase();
  const known = zones.get(key);
  if (known) {
    // Most recently used last, so the zones people actually send stay.
    zones.delete(key);
    zones.set(key, known);
    return known;
  }
  if (rejected.has(key)) return undefined;
  let format: Intl.DateTimeFormat;
  try {
    format = new Intl.DateTimeFormat('en-US', {
      timeZone: name,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
    });
  } catch {
    if (rejected.size >= REJECTED_MAX) {
      const oldest = rejected.values().next();
      if (!oldest.done) rejected.delete(oldest.value);
    }
    rejected.add(key);
    return undefined;
  }
  const zone = { name: format.resolvedOptions().timeZone, format };
  if (zones.size >= ZONES_MAX) {
    for (const oldest of zones.keys()) {
      if (oldest === DEFAULT_KEY) continue;
      zones.delete(oldest);
      break;
    }
  }
  zones.set(key, zone);
  return zone;
}

/** Test seam: how many zone formatters are kept, and whether the default zone is among them. */
export function zoneCacheStateForTests(): { size: number; hasDefault: boolean } {
  return { size: zones.size, hasDefault: zones.has(DEFAULT_KEY) };
}

/**
 * The zone a request's day is judged in: the IANA name the app sent, as Intl spells it, or DAY_DEFAULT_ZONE when it
 * sent none or anything else (too long, an offset, a path, a name Intl does not know). The zone is trusted for nothing
 * but where a person's day begins and ends.
 */
export function resolveZone(raw: unknown): string {
  if (typeof raw !== 'string' || raw.length < 1 || raw.length > 64 || !ZONE_RE.test(raw)) return DAY_DEFAULT_ZONE;
  return zoneFor(raw)?.name ?? DAY_DEFAULT_ZONE;
}

function zoneOrDefault(name: string): Zone {
  return zoneFor(name) ?? zoneFor(DAY_DEFAULT_ZONE)!;
}

/** The calendar date and wall-clock time in a zone at an instant (seconds; Intl drops the milliseconds). */
function clockIn(zone: Zone, ms: number): { year: number; month: number; day: number; hour: number; minute: number; second: number } {
  const clock = { year: 0, month: 0, day: 0, hour: 0, minute: 0, second: 0 };
  for (const part of zone.format.formatToParts(ms)) {
    if (Object.hasOwn(clock, part.type)) clock[part.type as keyof typeof clock] = Number(part.value);
  }
  clock.hour %= 24; // an engine that writes midnight as 24
  return clock;
}

function civilIn(zone: Zone, ms: number): number {
  const clock = clockIn(zone, ms);
  return civilOf(clock.year, clock.month, clock.day);
}

function offsetIn(zone: Zone, ms: number): number {
  const clock = clockIn(zone, ms);
  return Date.UTC(clock.year, clock.month - 1, clock.day, clock.hour, clock.minute, clock.second) - Math.floor(ms / 1000) * 1000;
}

/** The civil date in a zone at an instant: a person's "today". */
export function civilAt(zoneName: string, ms: number): number {
  return civilIn(zoneOrDefault(zoneName), ms);
}

/**
 * The first instant whose date in the zone is `civil`: its midnight, or, where the clocks skip midnight that day
 * (America/Santiago, Asia/Beirut, America/Havana change at midnight), the moment the day begins.
 */
export function startOfCivil(civil: number, zoneName: string): number {
  const zone = zoneOrDefault(zoneName);
  const key = `${zone.name}|${civil}`;
  const known = starts.get(key);
  if (known !== undefined) return known;
  // The wall time read as UTC, moved by the zone's offset; the offset is read again at that guess, in case the first
  // reading fell on the other side of a daylight-saving change. Checked: the date there is `civil` and a minute
  // earlier it is the day before; otherwise (a midnight the clocks skip) searched for.
  const wall = civil * DAY;
  const guess = wall - offsetIn(zone, wall);
  let start = wall - offsetIn(zone, guess);
  if (civilIn(zone, start) !== civil || civilIn(zone, start - MINUTE) !== civil - 1) start = searchStart(zone, civil, wall);
  if (starts.size >= STARTS_MAX) starts.clear();
  starts.set(key, start);
  return start;
}

/**
 * The first minute whose date in the zone is `civil` or later. A zone's date only moves forward, and every zone is
 * between UTC-12 and UTC+14, so the answer lies between 16 hours before the wall time and 14 hours after it.
 */
function searchStart(zone: Zone, civil: number, wall: number): number {
  let low = Math.floor((wall - 16 * HOUR) / MINUTE); // date before `civil` everywhere
  let high = Math.ceil((wall + 14 * HOUR) / MINUTE); // date `civil` or later everywhere
  while (high - low > 1) {
    const middle = Math.floor((low + high) / 2);
    if (civilIn(zone, middle * MINUTE) >= civil) high = middle;
    else low = middle;
  }
  return high * MINUTE;
}

/**
 * The instant at which a zone's clocks read `minuteOfDay` (minutes after midnight, 0 to 1439) on the date `civil`: the
 * wall-clock time of an outfit rotation's daily change (desktopMascotRotation.ts: "at 09:00 in your time zone"). Where
 * the clocks skip that time (a spring-forward hour, or a midnight some zones skip), the first instant at or after it;
 * where they repeat it (a fall-back hour), the first of the two.
 */
export function instantOfWallClock(civil: number, minuteOfDay: number, zoneName: string): number {
  const zone = zoneOrDefault(zoneName);
  const wall = civil * DAY + minuteOfDay * MINUTE;
  // The wall time read as UTC, moved by the zone's offset, read again at that guess (startOfCivil's method).
  const guess = wall - offsetIn(zone, wall);
  let instant = wall - offsetIn(zone, guess);
  const wanted = civil * 1440 + minuteOfDay;
  if (wallMinuteIn(zone, instant) === wanted) {
    // A repeated hour (the clocks fell back; the guess may land on either reading, east of UTC on the second): the first
    // of the two, one hour (or half an hour, Lord Howe) earlier while that still reads the same wall time.
    for (const step of [HOUR, 30 * MINUTE]) {
      while (wallMinuteIn(zone, instant - step) === wanted) instant -= step;
    }
    return instant;
  }
  // A time the clocks skip: the first minute whose wall clock reads the time or later.
  let low = Math.floor((wall - 16 * HOUR) / MINUTE);
  let high = Math.ceil((wall + 14 * HOUR) / MINUTE);
  while (high - low > 1) {
    const middle = Math.floor((low + high) / 2);
    if (wallMinuteIn(zone, middle * MINUTE) >= wanted) high = middle;
    else low = middle;
  }
  return high * MINUTE;
}

/** A zone's wall clock at an instant as one number: the civil date times 1440 plus the minute of the day. */
function wallMinuteIn(zone: Zone, ms: number): number {
  const clock = clockIn(zone, ms);
  return civilOf(clock.year, clock.month, clock.day) * 1440 + clock.hour * 60 + clock.minute;
}

/**
 * The window clients receive for the days from startCivil (included) to endCivil (not included) in a zone: always a
 * 'between' window, never a rule. Undefined if the zone has no such days (a calendar date some zone skipped).
 */
export function dayWindow(startCivil: number, endCivil: number, zoneName: string): { kind: 'between'; startMs: number; endMs: number } | undefined {
  const startMs = startOfCivil(startCivil, zoneName);
  const endMs = startOfCivil(endCivil, zoneName);
  return startMs < endMs ? { kind: 'between', startMs, endMs } : undefined;
}

/**
 * From when to when those days are going on somewhere on Earth: from their start in UTC+14 to their end in UTC-12. A day
 * outfit can be sold once its last day is over everywhere (decision 5: from 12:00 UTC the day after).
 */
export function everywhereWindow(startCivil: number, endCivil: number): { startMs: number; endMs: number } {
  return { startMs: startCivil * DAY - 14 * HOUR, endMs: endCivil * DAY + 12 * HOUR };
}

// ---------------------------------------------------------------------------------------------------------- greeting

/**
 * Characters a greeting may never hold: C0 and C1 controls (newlines and tabs included), DEL, the line and paragraph
 * separators, bidirectional controls (the Arabic letter mark U+061C included), and invisible characters that are not
 * part of an emoji: the soft hyphen, the Mongolian vowel separator, zero-width space, word joiner and the invisible
 * operators U+2061-U+2064, the byte order mark, and the interlinear annotation marks U+FFF9-U+FFFB. The ZWJ U+200D is
 * allowed, so "👩‍💻" stays one emoji.
 */
const GREETING_REFUSED = /[\u0000-\u001f\u007f-\u009f\u00ad\u061c\u180e\u200b\u200e\u200f\u2028\u2029\u202a-\u202e\u2060-\u2064\u2066-\u2069\ufeff\ufff9-\ufffb]/;
/** Tag characters (U+E0000-U+E007F) are invisible: allowed only inside a flag emoji's tag sequence (black flag, tags, cancel tag). */
const TAG_CHARACTER = /[\u{E0000}-\u{E007F}]/u;
const FLAG_TAG_SEQUENCE = /\u{1F3F4}[\u{E0020}-\u{E007E}]+\u{E007F}/gu;
const LONE_SURROGATE = /[\ud800-\udbff](?![\udc00-\udfff])|(?<![\ud800-\udbff])[\udc00-\udfff]/;

/**
 * A day greeting as people see it in place of "What can we work on?": NFC-normalised, trimmed, runs of spaces made
 * one, 1 to DAY_GREETING_MAX code points; undefined when it holds anything above or is not well-formed text. Clients
 * show it as plain text, never Markdown.
 */
export function sanitizeDayGreeting(raw: unknown): string | undefined {
  if (typeof raw !== 'string' || raw.length > 8 * DAY_GREETING_MAX) return undefined;
  if (LONE_SURROGATE.test(raw) || GREETING_REFUSED.test(raw) || TAG_CHARACTER.test(raw.replace(FLAG_TAG_SEQUENCE, ''))) return undefined;
  const text = raw.normalize('NFC').replace(/\s+/g, ' ').trim();
  const length = Array.from(text).length;
  return length >= 1 && length <= DAY_GREETING_MAX ? text : undefined;
}
